<script>
import { changeUserPassword } from '@/api/user.js';

export default {
    data() {
        return {
            passwordChangeData: {
                newPassword: '',
            },
            passwordErrorText: '',
            isPasswordChanging: false,
        };
    },
    computed: {
        userEditData() {
            return this.$store.getters.USER_EDIT_DATA;
        },
        passwordValidation() {
            const password = this.passwordChangeData.newPassword;
            return {
                minLength: password.length >= 8,
                hasNumber: /\d/.test(password),
                hasLowercase: /[a-z]/.test(password),
                hasUppercase: /[A-Z]/.test(password),
                hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>?~`]/.test(
                    password
                ),
            };
        },
        isPasswordValid() {
            const validation = this.passwordValidation;
            return (
                validation.minLength &&
                validation.hasNumber &&
                validation.hasLowercase &&
                validation.hasUppercase &&
                validation.hasSpecialChar
            );
        },
        canChangePassword() {
            return (
                this.isPasswordValid &&
                this.passwordChangeData.newPassword.trim() !== '' &&
                !this.isPasswordChanging
            );
        },
    },
    methods: {
        generatePassword() {
            const length = 12;
            const lowercase = 'abcdefghijklmnopqrstuvwxyz';
            const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
            const numbers = '0123456789';
            const specialChars = '!@#$%^&*()_+-=[]{}|;:,.<>?';

            let password = '';

            // Ensure at least one character from each required category
            password += lowercase[Math.floor(Math.random() * lowercase.length)];
            password += uppercase[Math.floor(Math.random() * uppercase.length)];
            password += numbers[Math.floor(Math.random() * numbers.length)];
            password +=
                specialChars[Math.floor(Math.random() * specialChars.length)];

            // Fill the rest with random characters from all categories
            const allChars = lowercase + uppercase + numbers + specialChars;
            for (let i = 4; i < length; i++) {
                password +=
                    allChars[Math.floor(Math.random() * allChars.length)];
            }

            // Shuffle the password to avoid predictable patterns
            password = password
                .split('')
                .sort(() => Math.random() - 0.5)
                .join('');

            // Set password field
            this.passwordChangeData.newPassword = password;

            // Clear any existing errors
            this.passwordErrorText = '';
        },
        showPasswordChangeConfirmation() {
            if (!this.canChangePassword) {
                return;
            }

            // Show confirmation dialog using the popup system
            this.$store.commit('SHOP_POPUP_NAME', 'confirmed');
            this.$store.commit(
                'CONFIRMED_POPUP_METHOD',
                this.changePassword.bind(this)
            );
        },
        async changePassword() {
            this.isPasswordChanging = true;
            this.passwordErrorText = '';

            try {
                const success = await changeUserPassword(
                    this.userEditData.id,
                    this.passwordChangeData.newPassword
                );

                if (success) {
                    this.passwordChangeData.newPassword = '';
                    this.$store.commit('POPUP_ADD', {
                        title: 'PASSWORD CHANGED',
                        description: ['Password changed successfully'],
                        color: '#00d25b',
                    });
                } else {
                    this.passwordErrorText =
                        'Failed to change password. Please try again.';
                }
            } catch (error) {
                this.passwordErrorText =
                    error.message ||
                    'Failed to change password. Please try again.';
            } finally {
                this.isPasswordChanging = false;
            }
        },
    },
};
</script>

<template>
    <div class="left_sub_panel_data">
        <input
            type="text"
            class="right_input mb_10"
            v-model="passwordChangeData.newPassword"
            placeholder="New Password"
        />

        <!-- Password Validation Info -->
        <div class="main_password_validation_info mb_10">
            <ul class="validation_list">
                <li
                    class="validation_item"
                    :class="{ valid: passwordValidation.minLength }"
                >
                    <span>•</span> 8 character minimum
                </li>
                <li
                    class="validation_item"
                    :class="{ valid: passwordValidation.hasNumber }"
                >
                    <span>•</span> one number
                </li>
                <li
                    class="validation_item"
                    :class="{ valid: passwordValidation.hasLowercase }"
                >
                    <span>•</span> one lowercase character
                </li>
                <li
                    class="validation_item"
                    :class="{ valid: passwordValidation.hasSpecialChar }"
                >
                    <span>•</span> one special character
                </li>
                <li
                    class="validation_item"
                    :class="{ valid: passwordValidation.hasUppercase }"
                >
                    <span>•</span> one uppercase character
                </li>
            </ul>
        </div>

        <!-- Password Action Buttons -->
        <div class="password_buttons_container mb_10">
            <button
                class="button_blue_100 generate_password_button"
                @click="generatePassword"
                type="button"
            >
                Generate Password
            </button>

            <button
                class="button_blue_100 change_password_button"
                :class="{ disabled: !canChangePassword }"
                :disabled="!canChangePassword || isPasswordChanging"
                @click="showPasswordChangeConfirmation"
                type="button"
            >
                {{ isPasswordChanging ? 'Changing...' : 'Change Password' }}
            </button>
        </div>

        <label v-if="passwordErrorText !== ''" class="error_text">{{
            passwordErrorText
        }}</label>
    </div>
</template>

<style scoped>
.main_password_validation_info {
    background-color: var(--main-color);
    border-radius: 4px;
    padding: 15px;
}

.validation_list {
    margin: 0;
    padding: 0;
    list-style: none;
}

.validation_item {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 13px;
}

.validation_item.valid {
    color: #00d25b;
    font-weight: 500;
}

.validation_item span {
    margin-right: 8px;
    font-weight: bold;
}

.password_buttons_container {
    display: flex;
    gap: 10px;
}

.generate_password_button {
    flex: 1;
}

.change_password_button {
    flex: 1;
}

.change_password_button.disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.change_password_button.disabled:hover {
    background-color: var(--main-color) !important;
}
</style>
